import {
  type ReportResponseDto,
  useReportsCreate,
} from '@/api/WhisApi/generated';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import {
  isOfflineSubmissionError,
  useOfflineReportSubmissionQueue,
} from '@/screens/ReportCreation/hooks/offlineReportSubmissionQueue/useOfflineReportSubmissionQueue';
import { useSupportingDocumentsUpload } from '@/screens/ReportCreation/hooks/useSupportingDocumentsUpload';
import {
  ReportFormData,
  ReportSubmissionMode,
} from '@/screens/ReportCreation/reportCreation.types';
import { buildCreateReportDto } from '@/screens/ReportCreation/reportCreation.utils';
import { useCallback } from 'react';

interface SubmitReportCallbacks {
  onSuccess?: (createdReport: ReportResponseDto) => void;
  onQueued?: () => void;
  onError?: () => void;
}

interface SubmitReportOptions {
  versionNote?: string;
}

interface UseReportSubmissionOptions {
  reportCode?: string;
}

interface UseReportSubmissionResult {
  isSubmitting: boolean;
  submitReport: (
    values: ReportFormData,
    mode: ReportSubmissionMode,
    callbacks?: SubmitReportCallbacks,
    options?: SubmitReportOptions,
  ) => Promise<void>;
}

export const useReportSubmission = ({
  reportCode,
}: UseReportSubmissionOptions): UseReportSubmissionResult => {
  const { t, language } = useAppTranslation();
  const { mutateAsync: createReport, isPending: isCreatingReport } =
    useReportsCreate({
      mutation: {
        networkMode: 'always',
        retry: false,
      },
    });
  const { isUploadingDocuments, uploadSupportingDocuments } =
    useSupportingDocumentsUpload();
  const { enqueueReportSubmission } = useOfflineReportSubmissionQueue();

  const submitReport = useCallback(
    async (
      values: ReportFormData,
      mode: ReportSubmissionMode,
      callbacks?: SubmitReportCallbacks,
      options?: SubmitReportOptions,
    ): Promise<void> => {
      try {
        const assets = await uploadSupportingDocuments(
          values.supportingDocuments,
        );
        const dto = buildCreateReportDto(values, mode, {
          code: reportCode,
          specifiedLanguage: language,
          ...(assets && { assets }),
          ...(options?.versionNote && { versionNote: options.versionNote }),
        });

        const createdReport = await createReport({ data: dto });
        callbacks?.onSuccess?.(createdReport);
      } catch (error) {
        if (isOfflineSubmissionError(error)) {
          await enqueueReportSubmission({
            values,
            mode,
            reportCode,
            versionNote: options?.versionNote,
          });
          callbacks?.onQueued?.();
          showToast({
            type: 'success',
            title: t('reportCreation.toasts.offlineQueueAdded'),
          });
          return;
        }

        console.error('Report submission aborted', error);
        if (callbacks?.onError) {
          callbacks.onError();
          return;
        }

        showToast({
          type: 'error',
          title: t('reportCreation.toasts.submitError'),
        });
      }
    },
    [
      createReport,
      language,
      reportCode,
      t,
      uploadSupportingDocuments,
      enqueueReportSubmission,
    ],
  );

  return {
    isSubmitting: isUploadingDocuments || isCreatingReport,
    submitReport,
  };
};
